import { create } from "axios";
import userManager from "../services/auth/openIdClient";

function createApiOptions() {
  const baseURL = process.env.REACT_APP_OPENID_AUTHORITY;
  return {
    baseURL,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Pragma: "no-cache",
    },
  };
}

// The login redirect this page has started, shared by every request that
// finds no token, so they start one redirect between them and all learn how
// it went.
let loginRedirect = null;

// Back from B2C can restore this page from the bfcache with module state
// intact; forget the old redirect so the next request can start a new one.
window.addEventListener("pageshow", (event) => {
  if (event.persisted) loginRedirect = null;
});

/*
 * Blocking-request tracking.
 *
 * A write the user is waiting on blocks the UI until it settles.
 *
 * Counted around the returned promise, not inside the interceptors. An
 * interceptor can only read a rejection's `config`, and a rejection that
 * carries none -- a payload axios cannot serialise, a malformed url -- would
 * leave the count above zero and the full-screen overlay stuck up for good,
 * with a reload the only way out. Reloading is exactly what destroys the
 * draft, so the overlay must never be able to outlive its request.
 */
let inFlightBlockingRequests = 0;
const blockingListeners = new Set();

// A listener that throws must not abort the request, skip its peers, or
// leave the count stranded -- it is a render, and renders fail.
const tellListener = (listener, blocked) => {
  try {
    listener(blocked);
  } catch (error) {
    console.error("blocking overlay listener failed", error);
  }
};

// Listeners hear about changes, not every count movement: a second write
// starting or finishing while another is still in flight changes nothing
// they should react to.
let lastNotifiedBlocked = false;
const notifyBlockingListeners = () => {
  const blocked = inFlightBlockingRequests > 0;
  if (blocked === lastNotifiedBlocked) return;
  lastNotifiedBlocked = blocked;
  blockingListeners.forEach((listener) => tellListener(listener, blocked));
};

// Subscribe to "is a write in flight"; returns an unsubscribe function.
export function subscribeToBlockingRequests(listener) {
  blockingListeners.add(listener);
  tellListener(listener, inFlightBlockingRequests > 0);
  return () => blockingListeners.delete(listener);
}

// Writes block by default. Fire-and-forget calls nobody is waiting on opt
// out with `{ background: true }`; GETs never block, so polling and
// reference fetches cannot freeze the page.
const isBlockingCall = (method, config) =>
  method !== "get" && config?.background !== true;

function trackBlocking(promise, method, config) {
  if (!isBlockingCall(method, config)) return promise;

  inFlightBlockingRequests += 1;
  notifyBlockingListeners();

  // The count stays truthful for as long as the request is on the wire --
  // the submit lock depends on that. Hiding a scrim that has been up too
  // long is the overlay's own decision (see BlockingOverlay).
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    inFlightBlockingRequests = Math.max(0, inFlightBlockingRequests - 1);
    notifyBlockingListeners();
  };

  return promise.then(
    (response) => {
      release();
      return response;
    },
    (error) => {
      release();
      throw error;
    },
  );
}

const addAuthorizationHeader = async (request) => {
  if (request.headers["Authorization"]) return request;

  let user = await userManager.getUser();

  if (user?.expired) {
    try {
      user = await userManager.signinSilent();
    } catch (error) {
      user = null;
    }
  }

  if (!user?.access_token) {
    // No usable token: the access token has aged out and B2C refused the
    // refresh -- a 400 invalid_grant on /token, or a silent iframe the
    // browser would not let complete. Re-authenticate rather than send the
    // request anonymously: an anonymous write comes back 401 and whatever
    // the user had typed is gone before anyone can tell them about it.
    if (!loginRedirect) {
      const started = userManager.signinRedirect({
        state: { redirectUrl: window.location.href },
      });
      loginRedirect = started;
      const forget = () => {
        if (loginRedirect === started) loginRedirect = null;
      };
      // Logged once here, not per waiter; console.error is what
      // Application Insights collects.
      started.then(
        () => {
          forget();
          console.error("Re-authentication did not leave the page");
        },
        (error) => {
          forget();
          console.error("Re-authentication did not start", error);
        },
      );
    }

    // A refusal (e.g. interaction_in_progress) rejects here with MSAL's own
    // error. Otherwise MSAL calls location.assign and resolves only after a
    // 30s timer (NavigationClient.defaultNavigateWindow), which a page that
    // leaves in time never reaches -- so getting past this line means the
    // navigation was cancelled or Back restored the page. Either way the
    // text is still on screen: fail loudly so the caller tells the user.
    await loginRedirect;
    throw new Error("Re-authentication did not leave the page");
  }

  request.headers["Authorization"] = `Bearer ${user.access_token}`;
  return request;
};

/*
 * A 401 sends the user to /unauthorized, except where the caller opted out.
 *
 * That default is deliberate: most 401s mean this account may not see this,
 * and the portal should say so rather than render an empty page. The
 * exemption is the explicit `tolerate401` flag, added for reference fetches
 * that must render a restricted-access placeholder without disturbing the
 * session (see safeReferenceRequest).
 *
 * What is gone is the `/proxy/` exemption that used to sit beside the flag.
 * It never worked: it tested error.config.url with startsWith("/proxy/"),
 * but axios 0.18 rewrites config.url to the absolute URL in dispatchRequest
 * before the response interceptor runs, so the check was always false.
 *
 * The redirect is only safe now because addAuthorizationHeader guarantees a
 * token: a 401 can no longer mean "we sent this request logged out", which
 * is what used to turn an expired session into a page navigation that threw
 * away whatever the user had typed.
 */
const onResponseError = (error) => {
  if (error?.response?.status === 401 && !error.config?.tolerate401) {
    const base = process.env.REACT_APP_BASE_URL;
    // Don't bounce a page that is already showing the unauthorized message.
    if (base && window?.location?.pathname !== "/unauthorized") {
      window.location.href = `${base}/unauthorized`;
    }
  }

  return Promise.reject(error);
};

function createAxiosApiInstance() {
  const api = create(createApiOptions());

  api.interceptors.request.use(addAuthorizationHeader);
  api.interceptors.response.use((response) => response, onResponseError);

  return api;
}

function createApi() {
  const axios = createAxiosApiInstance();

  return {
    get(url, config) {
      return axios.get(url, config);
    },
    post(url, data, config) {
      return trackBlocking(axios.post(url, data, config), "post", config);
    },
    put(url, data, config) {
      return trackBlocking(axios.put(url, data, config), "put", config);
    },
    delete(url, config) {
      return trackBlocking(axios.delete(url, config), "delete", config);
    },
    patch(url, data, config) {
      return trackBlocking(axios.patch(url, data, config), "patch", config);
    },
  };
}

export const api = createApi();
